import { Hand } from "lucide-react";
import { LOSS_REASONS, MEETING_STATUSES, SALE_TYPES, SOURCES, STATUSES } from "@/lib/constants";
import { fmtINR } from "@/lib/format";
import { earnings, repOptions } from "@/lib/leads";
import { Field, MoneyField, Section, SelectField, TextField } from "./Fields";

export const ContactSection = (p) => (
  <Section title="Contact" testid="editor-section-contact">
    <TextField {...p} k="name" label="Lead Name *" testid="editor-name-input" />
    <TextField {...p} k="company" label="Company" testid="editor-company-input" />
    <TextField {...p} k="email" label="Email" testid="editor-email-input" inputMode="email" />
    <TextField {...p} k="phone" label="Phone #" testid="editor-phone-input" inputMode="tel" />
    <TextField {...p} k="source" label="Source" testid="editor-source-input" list="source-options" />
    <datalist id="source-options">{SOURCES.map((s) => <option key={s} value={s} />)}</datalist>
  </Section>
);

export const PipelineSection = ({ reps, lead, ...p }) => {
  const toOpts = (list) => list.map((r) => ({ value: r.id, label: r.name }));
  return (
    <Section title="Pipeline & team" testid="editor-section-pipeline">
      <SelectField {...p} k="status" label="Status" testid="editor-status-select" options={STATUSES} placeholder="Select status" />
      {p.f.status === "Lost"
        ? <SelectField {...p} k="loss_reason" label="Loss Reason *" testid="editor-loss-reason-select" options={LOSS_REASONS} placeholder="Pick a reason" />
        : <div className="hidden sm:block" />}
      <SelectField {...p} k="setter_id" label="Setter" testid="editor-setter-select" options={toOpts(repOptions(reps, "setter", lead.setter_id))} placeholder="Unassigned" />
      <SelectField {...p} k="closer_id" label="Closer" testid="editor-closer-select" options={toOpts(repOptions(reps, "closer", lead.closer_id))} placeholder="Unassigned" />
    </Section>
  );
};

export const DatesSection = (p) => (
  <Section title="Dates (IST)" testid="editor-section-dates">
    <TextField {...p} k="date_created" type="datetime-local" label="Date Created" testid="editor-date-created-input" />
    <TextField {...p} k="first_contact_at" type="datetime-local" label="First Contact Date/Time" testid="editor-first-contact-input" />
    <TextField {...p} k="date_booked" type="date" label="Date Meeting Booked" testid="editor-date-booked-input" />
    <TextField {...p} k="meeting_date" type="date" label="Date of Meeting" testid="editor-meeting-date-input" />
  </Section>
);

export const MeetingSection = (p) => (
  <Section title="Meeting outcome" testid="editor-section-meeting">
    <SelectField {...p} k="meeting_status" label="Meeting Status" testid="editor-meeting-status-select" options={MEETING_STATUSES} />
    <SelectField {...p} k="offer_made" label="Offer Made" testid="editor-offer-made-select" options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
    <SelectField {...p} k="sale_type" label="Sale Type" testid="editor-sale-type-select" options={SALE_TYPES} />
  </Section>
);

const Earning = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-800/50 px-3 py-2.5">
    <span className="field-label">{label}</span>
    <p data-testid={testid} className={`font-mono text-base font-semibold ${value < 0 ? "text-amber-400" : "text-slate-50"}`}>{fmtINR(value)}</p>
    {value < 0 && <span className="text-[11px] font-medium text-amber-400">Clawback</span>}
  </div>
);

export const MoneySection = ({ draft, ...p }) => (
  <Section title="Money" testid="editor-section-money">
    <MoneyField {...p} k="deposit_amount" label="Deposit Amount" testid="editor-deposit-amount-input" />
    <TextField {...p} k="deposit_date" type="date" label="Deposit Date" testid="editor-deposit-date-input" />
    <MoneyField {...p} k="total_deal_value" label="Total Deal Value" testid="editor-deal-value-input" />
    <MoneyField {...p} k="cash_collected" label="Cash Collected" testid="editor-cash-collected-input" />
    <TextField {...p} k="date_paid_in_full" type="date" label="Date Paid In Full" testid="editor-paid-in-full-input" />
    <MoneyField {...p} k="refund_amount" label="Refund/Clawback Amount" testid="editor-refund-input" />
    <MoneyField {...p} k="setter_pct" label="Setter Commission %" testid="editor-setter-pct-input" pct />
    <MoneyField {...p} k="closer_pct" label="Closer Commission %" testid="editor-closer-pct-input" pct />
    <Earning label="Setter Earnings" value={earnings(draft, "setter_pct")} testid="editor-setter-earnings" />
    <Earning label="Closer Earnings" value={earnings(draft, "closer_pct")} testid="editor-closer-earnings" />
  </Section>
);

export const FollowUpSection = ({ onTouch, touching, ...p }) => (
  <Section title="Follow-up" testid="editor-section-followup">
    <TextField {...p} k="last_touch_date" type="date" label="Last Touch Date" testid="editor-last-touch-input" />
    <Field label="Quick action">
      <button type="button" data-testid="editor-touch-today-btn" disabled={touching} onClick={onTouch} className="btn-subtle w-full">
        <Hand className="h-4 w-4" /> Touched today
      </button>
    </Field>
  </Section>
);
